'use client'

import React from 'react'

type Props = {
  children: React.ReactNode
  /** Short label for the section, used in the fallback copy and the console. */
  section?: string
}

type State = { error: Error | null }

/**
 * Keeps one broken section (a token grid, a holdings table) from taking the
 * whole page down with Next's generic "client-side exception" screen. The
 * error and its component stack are logged so the cause is visible in the
 * browser console.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(
      `[${this.props.section ?? 'section'}] render error:`,
      error,
      info.componentStack,
    )
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="font-sans text-base font-bold text-gray-0">
            This section hit an error and could not be shown.
          </p>
          <p className="max-w-md font-sans text-sm text-gray-6">
            {this.state.error.message}
          </p>
          <button
            type="button"
            className="rounded-full bg-gray-0 px-5 py-2 font-sans text-sm font-bold text-black"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
